import { TabPermission } from "./candidate-things";
import { isNil } from "lodash";

/**
 * The level a WHOLE tab of a sharing gets: what its sharing type allows. A sharing stores no bare tab
 * keys anymore -- every tab carries this level or its own field detail. A View sharing has no tab model at all,
 * hence `null`; an unknown type (a sharing still being created) gets the collaboration level, and is
 * normalized once its type is known.
 *
 * Plain strings rather than the `SharingTypeEnum` of either side, which share their values but not their type.
 */
export function wholeTabPermission(sharingType: string | null | undefined): TabPermission | null {
  switch (sharingType) {
    case "View":
      return null;
    case "ReadOnlyCollaboration":
      return TabPermission.Selected;
    default:
      return TabPermission.Enabled;
  }
}

/**
 * Whether a shared tab grants anything at all: a whole-tab level, or at least one listed field. A tab with neither
 * shares NOTHING -- it used to read as "whole tab, unrestricted", which let any entry that had
 * lost its level (an old matrix merge, a mask tab whose empty field list was stripped on save, an
 * unmigrated key list) open the tab completely. Deny by default, like an absent tab.
 */
export function isSharedTab(tab: { fullTabPermission?: unknown; sharedFields?: readonly unknown[] | null } | string): boolean {
  if (typeof tab !== "object" || isNil(tab)) {
    return false;
  }
  return !isNil(tab.fullTabPermission) || (tab.sharedFields?.length ?? 0) > 0;
}

/** `tabs` without the entries that share nothing, see {@link isSharedTab}. */
export function withoutUnsharedTabs<T extends { fullTabPermission?: unknown; sharedFields?: readonly unknown[] | null }>(
  tabs: readonly (T | string)[] | null | undefined
): T[] {
  return (tabs ?? []).filter((tab): tab is T => isSharedTab(tab));
}

/** Tab keys (organization defaults, system settings) as whole tabs of a sharing of `sharingType`, see
 *  {@link wholeTabPermission}. None for a View sharing. */
export function toWholeSharedTabs(
  keys: readonly string[] | null | undefined,
  sharingType: string | null | undefined
): { key: string; fullTabPermission: TabPermission }[] {
  const fullTabPermission = wholeTabPermission(sharingType);
  if (isNil(fullTabPermission)) {
    return [];
  }

  return (keys ?? []).map((key) => ({ key, fullTabPermission }));
}
